import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { environment } from 'environments/environment';

declare const Redoc: {
    init: (
        specOrSpecUrl: any,
        options?: Record<string, any>,
        element?: HTMLElement,
        callback?: () => void
    ) => void;
};

interface DocChild {
    id: string;
    label: string;
    description: string;
}

interface DocSection {
    id: string;
    label: string;
    icon: string;
    description: string;
    children: DocChild[];
}

@Component({
    selector: 'app-devloper-document',
    templateUrl: './devloper-document.html',
    styleUrls: ['./devloper-document.scss']
})
export class DevloperDocumentComponent implements OnInit, AfterViewInit {
    @ViewChild('redocContainer') redocContainer?: ElementRef<HTMLElement>;

    url = '';
    openApiSpec: any = null;
    specLoading = false;
    specLoadError = '';

    sections: DocSection[] = [
        {
            id: 'introduction',
            label: 'Introduction',
            icon: 'fa fa-book',
            description: 'Overview, authentication, and request limit guidance.',
            children: [
                {
                    id: 'overview',
                    label: 'Overview',
                    description: 'General introduction to Lemonmode API documentation.'
                },
                {
                    id: 'authentication',
                    label: 'Authentication',
                    description: 'Basic authentication format required for each request.'
                },
                {
                    id: 'throttling',
                    label: 'Throttling Limit',
                    description: 'Request-per-minute limits for API usage.'
                }
            ]
        },
        {
            id: 'apis',
            label: 'APIs',
            icon: 'fa fa-code',
            description: 'Tracking endpoints and status references.',
            children: [
                {
                    id: 'tracking-details',
                    label: 'Get Tracking Details',
                    description: 'Endpoint details for tracking by tracking number or order number.'
                },
                {
                    id: 'tracking-status',
                    label: 'Tracking Status Description',
                    description: 'Status codes and their descriptions.'
                }
            ]
        },
        {
            id: 'languages',
            label: 'Programming Languages',
            icon: 'fa fa-laptop-code',
            description: 'Sample integration snippets in multiple languages.',
            children: [
                {
                    id: 'php',
                    label: 'PHP',
                    description: 'Sample request in PHP.'
                },
                {
                    id: 'java',
                    label: 'Java',
                    description: 'Sample request in Java.'
                },
                {
                    id: 'python',
                    label: 'Python',
                    description: 'Sample request in Python.'
                },
                {
                    id: 'curl',
                    label: 'CURL',
                    description: 'Sample request in CURL.'
                },
                {
                    id: 'http',
                    label: 'HTTP',
                    description: 'Raw HTTP request example.'
                },
                {
                    id: 'csharp',
                    label: 'C#',
                    description: 'Sample request in C#.'
                }
            ]
        }
    ];

    selectedSection = 'introduction';
    selectedChild = 'overview';
    expandedSection = 'introduction';

    private readonly specUrl = 'assets/lemonmode-tracking-openapi.json';
    private readonly redocOptions = {
        hideDownloadButton: false,
        pathInMiddlePanel: true,
        requiredPropsFirst: true,
        sortPropsAlphabetically: true
    };

    constructor(private http: HttpClient) {}

    ngOnInit(): void {
        this.url = environment.domain_name;
    }

    ngAfterViewInit(): void {
        this.renderRedocIfNeeded();
    }

    toggleSection(sectionId: string): void {
        this.expandedSection = this.expandedSection === sectionId ? '' : sectionId;
    }

    selectSection(sectionId: string, childId?: string): void {
        this.selectedSection = sectionId;
        this.expandedSection = sectionId;

        if (childId) {
            this.selectedChild = childId;
            this.renderRedocIfNeeded();
            return;
        }

        const section = this.sections.find(item => item.id === sectionId);
        this.selectedChild = section?.children?.[0]?.id || '';
        this.renderRedocIfNeeded();
    }

    get selectedSectionLabelWithChild(): string {
        const section = this.sections.find(item => item.id === this.selectedSection);
        const child = section?.children.find(item => item.id === this.selectedChild);
        return child ? `${section?.label} / ${child.label}` : section?.label || 'Developer Documentation';
    }

    get selectedSectionDescription(): string {
        const section = this.sections.find(item => item.id === this.selectedSection);
        const child = section?.children.find(item => item.id === this.selectedChild);
        return child?.description || section?.description || 'Developer documentation details';
    }

    private renderRedocIfNeeded(): void {
        if (this.selectedSection !== 'apis' || this.selectedChild !== 'tracking-details') {
            return;
        }

        if (this.openApiSpec) {
            setTimeout(() => this.renderRedoc());
            return;
        }

        this.loadOpenApiSpec();
    }

    private loadOpenApiSpec(): void {
        if (this.specLoading) {
            return;
        }

        this.specLoading = true;
        this.specLoadError = '';

        this.http.get(this.specUrl).subscribe({
            next: (spec: any) => {
                this.openApiSpec = spec;
                this.specLoading = false;
                setTimeout(() => this.renderRedoc());
            },
            error: () => {
                this.specLoadError = `OpenAPI file not found at ${this.specUrl}.`;
                this.specLoading = false;
            }
        });
    }

    private renderRedoc(): void {
        if (!this.openApiSpec || !this.redocContainer?.nativeElement || typeof Redoc === 'undefined') {
            return;
        }

        this.redocContainer.nativeElement.innerHTML = '';
        Redoc.init(this.openApiSpec, this.redocOptions, this.redocContainer.nativeElement);
    }

    copyData(el: HTMLElement): void {
        const codeContent = el.textContent || '';
        const textarea = document.createElement('textarea');
        textarea.value = codeContent;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    }
}
